package com.islamicmedia.site.components

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import kotlin.js.json

/**
 * Web twin of the mobile paywall's ConfettiKit burst.
 *
 * ConfettiKit itself can't be used here: it renders through a Compose `Canvas`, and this site is
 * Compose HTML (DOM), not Compose Multiplatform. `canvas-confetti` is the DOM equivalent and gives
 * the same one-shot celebratory burst.
 *
 * Fires once on mount. Colours match the mobile party (app gold + teal).
 */
private const val CONFETTI_CDN =
    "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js"

private val CONFETTI_COLORS = arrayOf("#FFD970", "#E0A50E", "#C8962E", "#0E7C7B", "#14A3A0")

@Composable
fun ConfettiBurst() {
    // Unit key: fires exactly once for the lifetime of this composable, so the countdown ticking
    // once a second can't retrigger it.
    LaunchedEffect(Unit) {
        ensureConfettiLoaded { fireBurst() }
    }
}

/** Runs [onReady] once canvas-confetti is available, appending the CDN script AT MOST ONCE. */
private fun ensureConfettiLoaded(onReady: () -> Unit) {
    val existing = window.asDynamic().confetti
    if (existing != null && existing != undefined) {
        onReady()
        return
    }
    val pending = document.querySelector("script[data-confetti-loader]")
    if (pending != null) {
        pending.addEventListener("load", { onReady() })
        return
    }
    val script = document.createElement("script")
    script.setAttribute("data-confetti-loader", "true")
    script.asDynamic().src = CONFETTI_CDN
    script.asDynamic().onload = { onReady() }
    document.head?.appendChild(script)
}

private fun fireBurst() {
    val confetti = window.asDynamic().confetti
    if (confetti == null || confetti == undefined) return
    // Two angled cannons from the lower corners, mirroring the spread of the mobile burst.
    confetti(
        json(
            "particleCount" to 90,
            "spread" to 70,
            "startVelocity" to 45,
            "origin" to json("x" to 0.5, "y" to 0.45),
            "colors" to CONFETTI_COLORS,
            // Above the paywall modal, which sits at z-index 1000.
            "zIndex" to 2000
        )
    )
}
